import { Input, Popover, message } from "antd"
import { CheckOutlined, CloseOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

interface CareLabel {
    key: string
    label: string
    color: string
    id?: number
}

interface Props {
    labels: CareLabel[]
    activeLabels: string[]
    onToggle: (key: string, id?: number) => void
    /** Chỉ xem (không có quyền trả lời chat): hiện thẻ đang gắn, không cho sửa */
    readOnly?: boolean
}

/** Bỏ dấu tiếng Việt để tìm thẻ không cần gõ dấu */
const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D").toLowerCase()

/** Nền chip nhạt theo màu thẻ (màu API dạng #rrggbb); màu lạ thì dùng xám */
const tint = (color: string, alpha: string) => (/^#[0-9a-f]{6}$/i.test(color) ? `${color}${alpha}` : "#94a3b81f")

/**
 * Thẻ chăm sóc của khách trên ô soạn tin.
 * Chỉ hiện các thẻ đang gắn (chip chữ đậm, đủ tương phản), gắn thêm qua nút "+ Thẻ" có ô tìm.
 * Gắn / gỡ vẫn lưu ngay như trước, nhưng luôn báo kèm nút "Hoàn tác" để sửa khi bấm nhầm.
 */
export default function LabelBar({ labels, activeLabels, onToggle, readOnly }: Props) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState("")

    const active = useMemo(
        () => labels.filter((l) => activeLabels?.includes(l.key)),
        [labels, activeLabels]
    )
    const keyword = fold(search.trim())
    const visible = keyword ? labels.filter((l) => fold(l.label).includes(keyword)) : labels

    const toggle = (label: CareLabel) => {
        const adding = !activeLabels?.includes(label.key)
        onToggle(label.key, label.id)
        // Một thông báo cho thẻ vừa bấm; bấm Hoàn tác là bấm ngược lại đúng thẻ đó
        const msgKey = `care-label-${label.key}`
        message.open({
            key: msgKey,
            type: "success",
            duration: 4,
            content: (
                <span>
                    {t(adding ? "roomchat.label_added" : "roomchat.label_removed", { name: label.label })}
                    <button
                        type="button"
                        className="ml-2 font-medium text-[#3e91ab] hover:underline"
                        onClick={() => {
                            message.destroy(msgKey)
                            onToggle(label.key, label.id)
                        }}
                    >
                        {t("roomchat.undo")}
                    </button>
                </span>
            ),
        })
    }

    const picker = (
        <div className="w-[240px]">
            <Input
                size="small"
                allowClear
                autoFocus
                prefix={<SearchOutlined className="text-gray-400" />}
                placeholder={t("roomchat.label_search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-2"
            />
            <div className="max-h-[260px] overflow-y-auto overscroll-contain -mx-1" role="listbox" aria-multiselectable>
                {visible.map((label) => {
                    const checked = activeLabels?.includes(label.key)
                    return (
                        <button
                            type="button"
                            role="option"
                            aria-selected={checked}
                            key={label.key}
                            onClick={() => toggle(label)}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-[13px] text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] ${checked ? "font-medium" : ""}`}
                        >
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: label.color }} />
                            <span className="flex-1 min-w-0 truncate">{label.label}</span>
                            {checked && <CheckOutlined className="text-[#3e91ab] text-[12px]" />}
                        </button>
                    )
                })}
                {!visible.length && (
                    <div className="px-2 py-3 text-center text-[12px] text-gray-400">{t("roomchat.label_not_found")}</div>
                )}
            </div>
        </div>
    )

    return (
        <div className="flex items-center gap-1.5 flex-wrap min-h-[28px]">
            {active.map((label) => (
                <span
                    key={label.key}
                    className="inline-flex items-center gap-1.5 h-6 pl-2 pr-1.5 rounded-full border text-[12px] font-medium text-gray-800 dark:text-gray-100"
                    style={{ background: tint(label.color, "1f"), borderColor: tint(label.color, "66") }}
                >
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: label.color }} />
                    {label.label}
                    {!readOnly && (
                        <button
                            type="button"
                            aria-label={t("roomchat.label_remove", { name: label.label })}
                            onClick={() => toggle(label)}
                            className="w-4 h-4 rounded-full flex items-center justify-center text-gray-500 hover:text-red-500 hover:bg-white/70"
                        >
                            <CloseOutlined className="text-[9px]" />
                        </button>
                    )}
                </span>
            ))}

            {!active.length && (
                <span className="text-[12px] text-gray-400">{t("roomchat.label_none")}</span>
            )}

            {!readOnly && (
                <Popover
                    content={picker}
                    trigger="click"
                    open={open}
                    onOpenChange={(next) => {
                        setOpen(next)
                        if (!next) setSearch("")
                    }}
                    placement="topLeft"
                >
                    <button
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={open}
                        className="inline-flex items-center gap-1 h-6 px-2 rounded-full border border-dashed border-gray-300 dark:border-gray-600 text-[12px] text-gray-600 dark:text-gray-300 hover:border-[#3e91ab] hover:text-[#3e91ab]"
                    >
                        <PlusOutlined className="text-[10px]" />
                        {t("roomchat.label_add")}
                    </button>
                </Popover>
            )}
        </div>
    )
}
